import {
  Component,
  inject,
  input,
  output,
  signal,
  computed,
  ViewChild,
  OnDestroy,
  AfterViewInit,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Organization } from 'app/modules/organization/models/organization';
import { LoadAssignmentService } from 'app/modules/load-assignment/services/load-assignment.service';
import { Store } from '@ngxs/store';
import { AuthState } from '@core/store/auth/auth.state';

@Component({
  selector: 'hmt-first-class-org-filter',
  templateUrl: './hmt-first-class-org-filter.component.html',
  styleUrls: ['./hmt-first-class-org-filter.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HmtFirstClassOrgFilterComponent),
      multi: true,
    },
  ],
})
export class HmtFirstClassOrgFilterComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  private readonly loadAssignmentService = inject(LoadAssignmentService);
  private readonly store = inject(Store);
  private readonly destroy$ = new Subject<void>();

  // Inputs
  placeholder = input<string>('Search Organization');
  label = input<string>('Organization');
  disabled = input<boolean>(false);

  // Output for standalone mode
  organizationSelected = output<Organization>();

  // Internal state
  searchControl = new FormControl<string | Organization | null>(null);
  allOrganizations = signal<Organization[]>([]);
  filteredOrganizations = signal<Organization[]>([]);
  loading = signal<boolean>(false);
  currentPage = signal<number>(0);
  hasMore = signal<boolean>(true);
  currentOrgId = signal<string | null>(null);
  hasLoadedInitial = signal<boolean>(false);

  @ViewChild('orgAutocomplete', { static: false }) autocomplete!: MatAutocomplete;

  // ControlValueAccessor properties
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this.currentOrgId.set(this.store.selectSnapshot(AuthState.getOrgId));

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => {
        if (typeof value === 'string' || value === null) {
          this.loadOrganizations(value as string || '', 0);
        }
      });
  }

  ngAfterViewInit(): void {
    if (this.autocomplete) {
      this.autocomplete.opened.pipe(takeUntil(this.destroy$)).subscribe(() => {
        setTimeout(() => {
          const panel = this.autocomplete?.panel?.nativeElement;
          if (panel) {
            this.attachScrollListener(panel);
          }
        }, 100);
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onInputFocus(): void {
    if (!this.hasLoadedInitial()) {
      this.loadOrganizations('', 0);
      this.hasLoadedInitial.set(true);
    }
  }

  private loadOrganizations(searchKey: string, pageNumber: number): void {
    this.loading.set(true);

    this.loadAssignmentService
      .getOnBoardedOrganizationsByVertical('VERT-TRN', searchKey, pageNumber, 20)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (organizations: Organization[]) => {
          const orgsArray = organizations || [];
          const isSearching = searchKey && searchKey.trim() !== '';
          const filteredOrgs = orgsArray.filter(org => org.id !== this.currentOrgId());

          if (isSearching || pageNumber === 0) {
            this.allOrganizations.set(filteredOrgs);
            this.filteredOrganizations.set(filteredOrgs);
          } else {
            const currentOrgs = this.allOrganizations();
            const updatedOrgs = [...currentOrgs, ...filteredOrgs];
            this.allOrganizations.set(updatedOrgs);
            this.filteredOrganizations.set(updatedOrgs);
          }

          this.hasMore.set(isSearching ? false : orgsArray.length === 20);
          this.currentPage.set(pageNumber);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading organizations:', error);
          this.loading.set(false);
          if (pageNumber === 0) {
            this.allOrganizations.set([]);
            this.filteredOrganizations.set([]);
          }
        },
      });
  }

  private loadMoreOrganizations(): void {
    const searchKey = typeof this.searchControl.value === 'string' ? this.searchControl.value : '';
    this.loadOrganizations(searchKey, this.currentPage() + 1);
  }

  private attachScrollListener(panel: HTMLElement): void {
    const existingListener = (panel as any).scrollListener;
    if (existingListener) {
      panel.removeEventListener('scroll', existingListener);
    }

    let scrollTimeout: any;
    const throttledScrollListener = () => {
      if (!scrollTimeout) {
        scrollTimeout = setTimeout(() => {
          this.handleScroll(panel);
          scrollTimeout = null;
        }, 200);
      }
    };

    (panel as any).scrollListener = throttledScrollListener;
    panel.addEventListener('scroll', throttledScrollListener, { passive: true });
  }

  private handleScroll(panel: HTMLElement): void {
    const scrollTop = panel.scrollTop;
    const scrollHeight = panel.scrollHeight;
    const clientHeight = panel.clientHeight;

    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    const isNearBottom = distanceFromBottom <= 100;

    if (isNearBottom && this.hasMore() && !this.loading()) {
      this.loadMoreOrganizations();
    }
  }

  displayOrganization = (org: Organization | string | null): string => {
    if (!org) {
      return '';
    }
    if (typeof org === 'string') {
      return org;
    }
    return org.orgName || '';
  };

  onOrganizationOptionSelected(org: Organization): void {
    this.organizationSelected.emit(org);
    this.onChange(org.id || null);
    this.onTouched();
  }
  writeValue(value: string | null): void {
    if (value) {
      const org = this.allOrganizations().find(o => o.id === value);
      if (org) {
        this.searchControl.setValue(org, { emitEvent: false });
      }
    } else {
      this.searchControl.setValue(null, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable({ emitEvent: false });
    } else {
      this.searchControl.enable({ emitEvent: false });
    }
  }
}

